import { makeStyles, Theme, createStyles, Typography, Grid } from "@material-ui/core";
import * as React from "react";
import SimpleTable from "../../../components/SimpleTable";
import InstallationModel from "../../../core/models/Installation";
import { useHubEventDateWindow, useHubXValue } from "../../../core/hooks/useHubHooks";
import {
  EVENT_EXPORT_EXCEL_CONSUMPTION_HISTORY,
  EVENT_EXPORT_PDF_CONSUMPTION_HISTORY,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_FILTER_DATE_CHANGE,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_NORMALIZATION_CONFIG_CHANGE,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_POINT_CONFIG_CHANGE,
} from "../../../Events";
import {
  FETCH_EXCEL_FILE_CONSUMPTION_HISTORY,
  FETCH_MULTIPLE_INSTALLATIONS_CONSUMPTION,
  FETCH_PDF_FILE_FROM_HTML,
} from "../../../Fedx";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { ConsumptionReadings } from "../../../core/models/ConsuptionReadings";
import { InstallationConsumptionServiceFetchParams } from "../../../services/InstallationConsumption";
import { formatDateTime } from "../../../helpers/datetime";
import { HOURS_BETWEEN_SLOTS } from "../../../core/constants/businnesConstants";
import { formatValue, isArrayValid } from "../../../helpers/utility";
import { aggregateConsumptionReadingsDataByDay } from "../../../helpers/consumptionStatisticalHelpers";
import { IConsumptionReading } from "../../../core/contracts/IConsumption";
import format from "date-fns/format";
import orderBy from "lodash/orderBy";
import { IExportExcel } from "../../../core/contracts/IExportExcel";
import IHtml from "../../../core/contracts/IHtml";
import { renderToString } from "react-dom/server";
import ReportGrid from "../../../components/Report/ReportGrid";
import CustomerModel from "../../../core/models/Customer";
import ReportSimpleTable from "../../../components/Report/ReportSimpleTable";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxHeight: "30vh",
    },

    installationHeader: {
      "margin": 0,
      "padding": theme.spacing(1),
      background: theme.palette.background.paper,
      color: theme.customPalette.text.boldgrey,
      "fontWeight": "bold",
      "& *": {
        fontWeight: "bold",
      },
    },
  })
);

type ReportConsumptionsProps = {
  filteredCustomers: CustomerModel[];
  filteredInstallationsIds: string[];
  statusFilter: string[];
};

type ReportDataType = {
  date: string;
  consumption: number;
  consumption_average: number;
};

const headers = ["Data", "Consumo", "Média de Consumo por Hora"];

function ReportConsumptions({
  filteredCustomers,
  filteredInstallationsIds,
  statusFilter,
}: ReportConsumptionsProps) {
  const [loading, setLoading] = React.useState(false);
  const [installationsConsumptionData, setInstallationsConsumptionData] = React.useState<
    ConsumptionReadings[] | null
  >(null);
  const [dailyConsumptionData, setDailyConsumptionData] = React.useState<
    | {
        id: string;
        readings: IConsumptionReading[];
      }[]
    | null
  >(null);

  const filterInterval = useHubEventDateWindow(
    EVENT_INSTALLATION_CONSUMPTION_HISTORY_FILTER_DATE_CHANGE,
    true
  );

  const classes = useStyles();
  const hub = useHubx();
  useHubXSubscribe(EVENT_EXPORT_EXCEL_CONSUMPTION_HISTORY, fetchFile);
  useHubXSubscribe(EVENT_EXPORT_PDF_CONSUMPTION_HISTORY, fetchPdf);
  const toastr = useSnackbar();

  const useNormalizedData = useHubXValue(
    EVENT_INSTALLATION_CONSUMPTION_HISTORY_NORMALIZATION_CONFIG_CHANGE,
    true
  );

  const useDataInSlot = useHubXValue(
    EVENT_INSTALLATION_CONSUMPTION_HISTORY_POINT_CONFIG_CHANGE,
    true
  );

  const getFileName = `_${formatDateTime(new Date(), "dd/MM/yyyy")}`;

  async function fetchFile() {
    let reportData: ReportDataType[];

    if (useDataInSlot) {
      reportData =
        installationsConsumptionData?.flatMap((installation) => {
          return (
            installation.readings?.map((consumption) => {
              return {
                date: formatDateTime(consumption.date),
                consumption: formatValue(
                  useNormalizedData ? consumption.level : consumption.raw_level,
                  2
                ),
                consumption_average: formatValue(
                  (useNormalizedData ? consumption.level : consumption.raw_level) /
                    HOURS_BETWEEN_SLOTS,
                  2
                ),
              } as ReportDataType;
            }) ?? []
          );
        }) ?? [];
    } else {
      reportData =
        dailyConsumptionData?.flatMap((installation) => {
          return (
            installation.readings?.flatMap((consumption) => {
              return {
                date: format(new Date(consumption.date), "dd/MM/yyyy"),
                consumption: formatValue(
                  useNormalizedData ? consumption.level : consumption.raw_level,
                  2
                ),
                consumption_average: formatValue(
                  (useNormalizedData ? consumption.level : consumption.raw_level) / 24,
                  2
                ),
              } as ReportDataType;
            }) ?? []
          );
        }) ?? [];
    }
    const exportFile: IExportExcel = {
      headers: headers,
      data: reportData,
      fileName: `Relatório_Consumo${getFileName}`,
    };

    await hub.request(FETCH_EXCEL_FILE_CONSUMPTION_HISTORY, exportFile);
  }

  async function fetchPdf() {
    const exportFile: IHtml = {
      htmlString: renderToString(
        <ReportGrid headerReport="Relatório de Consumo">
          {filteredCustomers.map((customer) => {
            return customer?.installations
              ?.filter((installation) => {
                if (isArrayValid(filteredInstallationsIds)) {
                  return filteredInstallationsIds.includes(installation.installation_id ?? "");
                }
                return false;
              })
              ?.flatMap((installation) => {
                const rows = buildRowsFromCustomer(installation);
                return (
                  <div
                    style={{
                      pageBreakAfter: "avoid",
                      pageBreakInside: "avoid",
                    }}
                    key={`${customer.customer_id}_${installation.installation_id}`}
                  >
                    <div>
                      <div
                        style={{
                          margin: 0,
                          padding: 8,
                          background: "#E7E7E7",
                          color: "#1D212A",
                          fontWeight: "bold",
                          display: "flex",
                          columnGap: "16px",
                        }}
                      >
                        <div>
                          <div>{customer.customer_name}</div>
                        </div>
                        <div>
                          <div>{installation.name}</div>
                        </div>
                        <div>
                          <div>
                            {installation.product_code
                              ? `Instalação de ${installation.product_code}`
                              : ""}
                          </div>
                        </div>
                        <div>
                          <div>{installation.installation_id}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ pageBreakAfter: "avoid" }}>
                      <ReportSimpleTable header={headers} rows={rows} customHeight="90%" />
                    </div>
                  </div>
                );
              });
          })}
        </ReportGrid>
      ),
      fileName: `Relatório_Consumo${getFileName}`,
    };

    await hub.request(FETCH_PDF_FILE_FROM_HTML, exportFile);
  }

  async function fetchData() {
    if (isArrayValid(filteredInstallationsIds)) {
      setLoading(true);

      try {
        const fetchParams: InstallationConsumptionServiceFetchParams[] = filteredInstallationsIds.map(
          (id) => {
            return {
              installation_id: id,
              startDate: filterInterval?.start || undefined,
              endDate: filterInterval?.end || undefined,
            };
          }
        );

        const APIInstallationResponse: ConsumptionReadings[] = await hub.request(
          FETCH_MULTIPLE_INSTALLATIONS_CONSUMPTION,
          fetchParams
        );

        const dailyData = APIInstallationResponse.map((x) => {
          const prevResult = x.readings as IConsumptionReading[];
          return {
            id: x.installation_id,
            readings: x?.readings ? (aggregateConsumptionReadingsDataByDay(prevResult)).readings : [],
          };
        });

        setDailyConsumptionData(dailyData);

        setInstallationsConsumptionData(APIInstallationResponse);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("InstallationConsumptionHistory → fetchData → error", error);
        toastr.enqueueSnackbar("Ops! Ocorreu um erro ao consumir os dados de consumo!", {
          variant: "error",
        });
      }
      setLoading(false);
    }
  }

  const buildRowsFromCustomer = (installation: InstallationModel) => {
    const installationData = installationsConsumptionData?.find(
      (x) => x.installation_id === installation.installation_id
    );
    if (installationData?.readings) {
      const uom = installationData.product_uom;

      if (useDataInSlot) {
        return orderBy(
          installationData.readings.map((d) => {
            return [
              formatDateTime(d.date),
              `${formatValue(useNormalizedData ? d.level : d.raw_level, 2)}${uom ? ` ${uom}` : ""}`,
              `${formatValue(
                (useNormalizedData ? d.level : d.raw_level) / HOURS_BETWEEN_SLOTS,
                2
              )} ${uom ? `${uom}/h` : ""}`,
            ];
          }),
          "[0]",
          "desc"
        );
      } else {
        const data = dailyConsumptionData?.find((x) => x.id === installation.installation_id);
        if (data?.readings) {
          return orderBy(
            data.readings.map((d: IConsumptionReading) => {
              return [
                format(new Date(d.date), "dd/MM/yyyy"),
                `${formatValue(useNormalizedData ? d.level : d.raw_level, 2)}${
                  uom ? ` ${uom}` : ""
                }`,
                `${formatValue((useNormalizedData ? d.level : d.raw_level) / 24, 2)} ${
                  uom ? `${uom}/h` : ""
                }`,
              ];
            }) || [],
            "[0]",
            "desc"
          );
        }
        return [];
      }
    }
    return [];
  };

  const memoDataToDisplay = () => {
    if (isArrayValid(filteredCustomers)) {
      return filteredCustomers.map((customer) => {
        return customer?.installations
          ?.filter((installation) => {
            if (isArrayValid(filteredInstallationsIds)) {
              return filteredInstallationsIds.includes(installation.installation_id ?? "");
            }
            return false;
          })
          ?.flatMap((installation) => {
            const rows = buildRowsFromCustomer(installation);

            return (
              <div key={`${customer.customer_id}_${installation.installation_id}`}>
                <div>
                  <Grid className={classes.installationHeader} container spacing={4}>
                    <Grid item>
                      <Typography component="h5" variant="h5">
                        {customer.customer_name}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography component="h5" variant="h5">
                        {installation.name}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography component="h5" variant="h5">
                        {installation.product_code
                          ? `Instalação de ${installation.product_code}`
                          : ""}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography component="h5" variant="h5">
                        {installation.installation_id}
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
                <SimpleTable header={headers} rows={rows} loading={loading} />
              </div>
            );
          });
      });
    }
  };

  React.useEffect(() => {
    const doFetch = async () => await fetchData();
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInstallationsIds, filterInterval]);

  return (
    <div className={classes.root}>
      {memoDataToDisplay() || <SimpleTable header={headers} rows={[]} />}
    </div>
  );
}

export default ReportConsumptions;
